/**
 * Mana Locals Admin — the Lakshi assistant's boundary schemas.
 *
 * CONTRACT SOURCE: the M71 assistant contract published by the API repository
 * (`src/domains/m71-ai-agent-runtime/contracts/assistant.schemas.ts`). The console
 * owns its own copy of the shapes — there is no build-time dependency on the API
 * repository — and this file is that copy. Every shape here is parsed on the wire
 * by `src/api/client/http.ts`.
 *
 * ---------------------------------------------------------------------------
 * THE DRAFT IS DATA, NEVER A WRITE
 * ---------------------------------------------------------------------------
 * `AssistantDraft` carries the values a model proposed for a form. Nothing in this
 * file mutates a business record, and no field on it names a business record. The
 * console shows a draft, a person reviews it, and the ordinary form performs the
 * write — the assistant never gets to press the button.
 *
 * ---------------------------------------------------------------------------
 * THE FACTS ARE ALREADY MASKED
 * ---------------------------------------------------------------------------
 * `AssistantFact.value` arrives sanitised from the server, and `masked` says
 * whether the sanitiser had to mask it. The console renders the value it was
 * given and never re-derives one, so a masked value cannot be un-masked by a
 * second guess here.
 */
import { z } from 'zod';

/** Who the agent is. The name is a constant of the product, not a setting. */
export const AssistantAgentSchema = z.object({
  name: z.string().min(1),
  tagline: z.string().min(1),
});

export type AssistantAgent = z.infer<typeof AssistantAgentSchema>;

/**
 * The skill that speaks for the page the caller is on.
 *
 * `suggestions` are the prefilled messages the console OFFERS. They are never sent
 * on their own: the person may edit one before it is sent.
 */
export const AssistantSkillSchema = z.object({
  id: z.string().min(1),
  module: z.string().min(1),
  label: z.string().min(1),
  suggestions: z.array(z.string().min(1)),
  /** The capability this page can draft for, when it can draft one. */
  draftCapability: z.string().nullable(),
});

export type AssistantSkill = z.infer<typeof AssistantSkillSchema>;

/**
 * One sanitised fact about the page.
 *
 * `value` is a STRING on the wire even when the fact is a number: the console
 * renders facts, and a fact that changes shape between modules is a fact the
 * console has to special-case.
 */
export const AssistantFactSchema = z.object({
  /** The fact's name. It names the fact, never the row it came from. */
  key: z.string().min(1),
  /** The label the person reads. */
  label: z.string().min(1),
  /** The value, already sanitised by the server. */
  value: z.string(),
  /** Whether the sanitiser masked this value. */
  masked: z.boolean(),
});

export type AssistantFact = z.infer<typeof AssistantFactSchema>;

/** The route-aware context for the page the caller is on. */
export const AssistantContextSchema = z.object({
  agent: AssistantAgentSchema,
  /** The route the caller is on. */
  route: z.string(),
  /** The skill for that route, when the route belongs to one. */
  skill: AssistantSkillSchema.nullable(),
  /** The facts the agent may know on this page. Empty when the page declares none. */
  facts: z.array(AssistantFactSchema),
});

export type AssistantContext = z.infer<typeof AssistantContextSchema>;

/** One conversation. */
export const AssistantThreadSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  /** The route the conversation is anchored to. */
  route: z.string(),
  messageCount: z.number().int().nonnegative(),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
});

export type AssistantThread = z.infer<typeof AssistantThreadSchema>;

/**
 * The form draft the model produced.
 *
 * `fields` are the proposed values, keyed by the FORM's field names; `review` is
 * the sentence the console shows above them. The values arrive masked.
 */
export const AssistantDraftSchema = z.object({
  /** The capability the draft is for, e.g. `tenant.create`. */
  capability: z.string().min(1),
  /** The proposed values, keyed by the form's field names. */
  fields: z.record(z.string(), z.string()),
  /** The sentence the console shows above the values. */
  review: z.string().min(1),
});

export type AssistantDraft = z.infer<typeof AssistantDraftSchema>;

/** One message in the conversation. */
export const AssistantMessageSchema = z.object({
  id: z.string().min(1),
  role: z.enum(['user', 'assistant']),
  /** The text. The person's own words, or the agent's Markdown answer. */
  content: z.string(),
  /** The draft the agent produced, when it produced one. */
  draft: AssistantDraftSchema.nullable(),
  createdAt: z.string().min(1),
});

export type AssistantMessage = z.infer<typeof AssistantMessageSchema>;

/** What one turn cost. */
export const AssistantUsageSchema = z.object({
  promptTokens: z.number().int().nonnegative().nullable(),
  completionTokens: z.number().int().nonnegative().nullable(),
  totalTokens: z.number().int().nonnegative().nullable(),
  /** How many requests this turn counted as, when the provider reported no token count. */
  requests: z.number().int().nonnegative(),
});

export type AssistantUsage = z.infer<typeof AssistantUsageSchema>;

/** The answer to one turn: the agent's message, and what it cost. */
export const AssistantTurnSchema = z.object({
  message: AssistantMessageSchema,
  usage: AssistantUsageSchema,
});

export type AssistantTurn = z.infer<typeof AssistantTurnSchema>;

/* --------------------------------------------------------------------------- */
/* THE REQUESTS                                                                 */
/* --------------------------------------------------------------------------- */

/** The route the context is read for. It selects the skill. */
export const AssistantContextInputSchema = z.object({
  route: z.string().min(1).max(300),
});

export type AssistantContextInput = z.infer<typeof AssistantContextInputSchema>;

/** Opens a conversation, anchored to a route. */
export const AssistantThreadInputSchema = z.object({
  route: z.string().min(1).max(300),
  /** The title. The agent writes one when the caller does not. */
  title: z.string().min(1).max(200).optional(),
});

export type AssistantThreadInput = z.infer<typeof AssistantThreadInputSchema>;

/** Sends one turn. */
export const AssistantMessageInputSchema = z.object({
  message: z.string().min(1).max(8_000),
  /** The route the turn happened on. It selects the skill and the context. */
  route: z.string().min(1).max(300),
});

export type AssistantMessageInput = z.infer<typeof AssistantMessageInputSchema>;
